import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Typography, withStyles } from '@material-ui/core'
import MuiAccordion from '@material-ui/core/Accordion'
import MuiAccordionSummary from '@material-ui/core/AccordionSummary'
import MuiAccordionDetails from '@material-ui/core/AccordionDetails'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SunEditor from 'suneditor-react'
import 'suneditor/dist/css/suneditor.min.css'
import { mediaImageUploadAction } from 'src/reducers/media-image/action'
import URL from 'src/api/url'

const Accordion = withStyles({
  root: {
    width: '100%',
    boxShadow: 'none',
    borderRadius: '8px',
    backgroundColor: '#F4F6F8',
    '&:not(:last-child)': { borderBottom: 0 },
    '&:before': { display: 'none' },
    '&$expanded': {
      margin: 'auto',
      boxShadow: 'none',
      backgroundColor: '#F4F6F8',
    },
  },
  expanded: { backgroundColor: '#F4F6F8' },
})(MuiAccordion)

const AccordionSummary = withStyles({
  root: {
    backgroundColor: '#F4F6F8',
    minHeight: 56,
    borderRadius: '8px',
    gap: 8,
    '&$expanded': {
      minHeight: 56,
      gap: 8,
      backgroundColor: '#F4F6F8',
    },
  },
  content: {
    '&$expanded': {
      margin: '12px 0',
      backgroundColor: '#F4F6F8',
    },
  },
})(MuiAccordionSummary)

const AccordionDetails = withStyles(theme => ({
  root: {
    backgroundColor: '#F4F6F8',
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    borderRadius: '8px',
  },
}))(MuiAccordionDetails)

const styles = () => ({
  heading: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#002E47',
  },
  description: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#637381',
  },
})

const editorOptions = {
  mode: 'inline',
  formats: ['h1', 'h2', 'p'],
  placeholder: 'This message is shown to the learner after the payment attempt.',
  height: 214,
  buttonList: [
    [],
    ['formatBlock'],
    [],
    ['bold'],
    [],
    ['image'],
    [],
    ['codeView'],
    [],
    [
      ':i-More Misc-default.more_vertical',
      'fontColor',
      'hiliteColor',
      'align',
      'link',
      'italic',
      'underline',
      'removeFormat',
    ],
  ],
  pasteTagsWhitelist: 'p|h1|h2|h3',
  imageMultipleFile: false,
  imageUrlInput: false,
  imageAlignShow: false,
  imageHeightShow: false,
  imageResizing: false,
  popupDisplay: 'local',
  attributesWhitelist: {
    all:
      'style|dir|class|width|height|title|allow|allowfullscreen|aspectratio|id|frameborder|iframe|className|data-.+',
  },
}

const DEFAULT_SUCCESS = 'Thanks! Your payment was received.'
const DEFAULT_FAILURE = 'We couldn’t complete your payment. Please try again or contact support.'

const PaymentMessageEditor = ({ classes, messages, onPatch }) => {
  const dispatch = useDispatch()

  const [expanded, setExpanded] = useState(false)
  const [successMessage, setSuccessMessage] = useState(
    messages?.success ?? DEFAULT_SUCCESS,
  )
  const [failureMessage, setFailureMessage] = useState(
    messages?.failure ?? DEFAULT_FAILURE,
  )

  useEffect(() => {
    if (messages?.success != null) setSuccessMessage(messages.success)
  }, [messages?.success])

  useEffect(() => {
    if (messages?.failure != null) setFailureMessage(messages.failure)
  }, [messages?.failure])

  const handleImageUpload = (files, info, uploadHandler) => {
    dispatch(
      mediaImageUploadAction(files[0], '', result => {
        uploadHandler({
          result: [{ url: `${URL.MEDIA}${result.url}`, name: result.name, size: 100 }],
        })
      }),
    )
  }

  return (
    <Accordion expanded={expanded}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        onClick={() => setExpanded(prev => !prev)}
      >
        <Typography className={classes.heading}>
          Customize Payment Messages
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Typography className={classes.description}>
          Message displayed after a successful payment
        </Typography>
        <SunEditor
          key="payment-success-editor"
          defaultValue={successMessage}
          value={successMessage}
          onChange={value => setSuccessMessage(value)}
          onBlur={(_event, editorContents) =>
            onPatch && onPatch('messageOnSuccess', editorContents)
          }
          onImageUploadBefore={handleImageUpload}
          setOptions={editorOptions}
        />

        <Typography className={classes.description} style={{ marginTop: 16 }}>
          Message displayed after a failed or canceled payment
        </Typography>
        <SunEditor
          key="payment-failure-editor"
          defaultValue={failureMessage}
          value={failureMessage}
          onChange={value => setFailureMessage(value)}
          onBlur={(_event, editorContents) =>
            onPatch && onPatch('messageOnFailure', editorContents)
          }
          onImageUploadBefore={handleImageUpload}
          setOptions={editorOptions}
        />
      </AccordionDetails>
    </Accordion>
  )
}

export default withStyles(styles)(PaymentMessageEditor)
